import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { MicroFrontendLoader } from './MicroFrontendLoader';
import type {
  IsolatedMicroFrontendAPI,
  IsolatedMicroFrontendType,
  MicroFrontendType,
} from './MicroFrontendTypes';

declare const System: { import: (moduleName: string) => Promise<any> };

const MicroFrontendWrapper = memo(
  ({
    Component,
    ...props
  }: {
    Component: ReturnType<MicroFrontendType['component']>;
    [key: string]: any;
  }) => (Component ? <Component {...props} /> : null),
);

const SelfRenderedMicroFrontend = memo(
  ({
    moduleName,
    render,
    unmount,
    ...props
  }: IsolatedMicroFrontendAPI & { moduleName: string; [key: string]: any }) => {
    const rootNodeRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
      render(rootNodeRef.current as HTMLDivElement, props);
    });
    useLayoutEffect(
      () => () => unmount(),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    );
    return <div ref={rootNodeRef} id={`root_${moduleName}`} />;
  },
);

export function MicroFrontendBootstraper({
  moduleName,
  withSpinner,
  ...props
}: {
  moduleName: string;
  withSpinner?: boolean;
  [key: string]: any;
}) {
  const [[Component, dependencies, isolatedAPI], setMFE] = useState<
    [
      ReturnType<MicroFrontendType['component']> | null,
      string[],
      IsolatedMicroFrontendAPI | null,
    ]
  >([null, [], null]);
  const [isLoading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;
    System.import(moduleName)
      .then(
        (module: {
          default: MicroFrontendType | IsolatedMicroFrontendType;
        }) => {
          if (!isMounted) {
            return;
          }
          const microFrontend = module.default;
          setLoading(false);
          if (typeof microFrontend.bootstrap === 'function') {
            microFrontend.bootstrap();
          }
          const dependencies: string[] =
            'dependencies' in microFrontend ? microFrontend.dependencies() : [];
          if ('component' in microFrontend) {
            setMFE([microFrontend.component(), dependencies, null]);
          } else if ('render' in microFrontend) {
            setMFE([
              null,
              dependencies,
              { render: microFrontend.render, unmount: microFrontend.unmount },
            ]);
          } else {
            console.error(
              `No component nor render function found in ${moduleName}`,
            );
          }
        },
      )
      .catch((error: Error) => {
        console.error(error);
      });
    return () => {
      isMounted = false;
    };
  }, [moduleName]);

  const componentProps: { [key: string]: any } = dependencies
    ? Object.fromEntries(dependencies.map((key) => [key, props[key]]))
    : {};

  return (
    <>
      {isLoading && withSpinner ? <MicroFrontendLoader /> : null}
      {Component ? (
        <MicroFrontendWrapper Component={Component} {...componentProps} />
      ) : isolatedAPI ? (
        <SelfRenderedMicroFrontend
          moduleName={moduleName}
          {...isolatedAPI}
          {...componentProps}
        />
      ) : null}
    </>
  );
}
